import {
  Body,
  CardBackground,
  ColumnHeaderCell,
  Combobox,
  Header,
  Row,
  Table,
  Typography,
} from "@hellocarbo/lasagne";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  data,
  LoaderFunctionArgs,
  useActionData,
  useLoaderData,
  useParams,
  useSubmit,
} from "react-router";
import invariant from "tiny-invariant";

import { ReviewBanner } from "@/components/Banner";
import { InternalComparison } from "@/components/Review/InternalComparison";
import {
  CategoryScore,
  TCategory,
  TDynamicCategory,
  TSimulation,
  TSubCategory,
  TTrackingCampaign,
} from "@/types";
import { formatEndDate } from "@/utils/formatDate";

import { useReviewContext } from "./Review";

export async function loader({ params }: LoaderFunctionArgs) {
  const reviewId = params.reviewId;
  invariant(reviewId, "No review found!");

  // Fetching the list of Tracking Campaigns we can offer to comparison
  const trackingCampaignsResponse = await fetch(
    `/api/client/tracking_campaigns/`
  );

  if (trackingCampaignsResponse.status !== 200) {
    throw data(`Failed to load the list of tracking campaigns.`, {
      status: trackingCampaignsResponse.status,
    });
  }

  const { data: trackingCampaignsData } =
    (await trackingCampaignsResponse.json()) as {
      data: TTrackingCampaign[];
    };

  const currentTrackingCampaign = trackingCampaignsData.find(
    (trackingCampaign) => trackingCampaign.review_id === Number(reviewId)
  );

  // Fetching the category_scores of the current Tracking Campaign
  const categoryScoreResponse = await fetch(
    `/api/client/scores/${currentTrackingCampaign?.score.id}/category_scores`
  );

  if (categoryScoreResponse.status !== 200) {
    throw data(
      `Failed to load the category scores for current tracking campaign.`,
      {
        status: categoryScoreResponse.status,
      }
    );
  }

  const { data: currentCategoryScoreData } =
    (await categoryScoreResponse.json()) as {
      data: CategoryScore[];
    };

  // Fetching the list of Simulations we can offer to comparison
  const simulationsResponse = await fetch(`/api/client/simulations/`);

  if (simulationsResponse.status !== 200) {
    throw data(`Failed to load the list of simulations.`, {
      status: simulationsResponse.status,
    });
  }

  const { data: simulationsData } = (await simulationsResponse.json()) as {
    data: TSimulation[];
  };

  // Fetching the general list of categories that are used in category_scores
  const categoriesResponse = await fetch(`/api/client/categories`);
  if (categoriesResponse.status !== 200) {
    throw data("Failed to load the categories", {
      status: categoriesResponse.status,
    });
  }
  const { data: categories } = (await categoriesResponse.json()) as {
    data: TDynamicCategory[];
  };

  // Transforming the flat list of categories in a 2-level-only hierarchy of categories
  // Meaning we will have only the top categories, and an array with all its children, grand-children, etc.
  const rootCategoriesById = categories.reduce<Record<number, TCategory>>(
    (acc, category) => {
      if (category.root_category?.id === category.id)
        acc[category.id] = { id: category.id, key: category.key, children: [] };
      return acc;
    },
    {} as Record<number, TCategory>
  );

  categories.forEach((category) => {
    // Skip intermediate categories, we only want to keep the root and leaf categories
    if (!(category.is_root || category.is_leaf)) {
      return;
    }
    if (
      category.root_category?.id &&
      category.id !== category.root_category?.id
    ) {
      const temp: TSubCategory = { id: category.id, key: category.key };
      rootCategoriesById[category.root_category?.id].children.push(temp);
    }
  });

  return {
    rootCategoriesById,
    currentCategoryScoreData,
    simulationsData,
    trackingCampaignsData,
  };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const scoreId = formData.get("scoreId");

  if (!scoreId) {
    return { ok: true, comparedCategoryScores: [] };
  }

  const response = await fetch(`/api/client/scores/${scoreId}/category_scores`);

  if (response.status !== 200) {
    return { ok: false };
  }

  const { data: comparedCategoryScores } = (await response.json()) as {
    data: CategoryScore[];
  };

  return { ok: true, comparedCategoryScores };
}

function ReviewInternalComparison() {
  const { t } = useTranslation(["common", "formatter", "review"]);
  const { reviewData } = useReviewContext();
  const { reviewId } = useParams();
  const {
    rootCategoriesById,
    currentCategoryScoreData,
    simulationsData,
    trackingCampaignsData,
  } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const submit = useSubmit();

  const { period_start: periodStart, period_end: periodEnd } = reviewData;

  // UI transformation needed for the Combobox
  const formatteTrackingCampaignsGroup = trackingCampaignsData
    .filter(
      (campaign) =>
        campaign.finished &&
        !campaign.score.is_external &&
        String(campaign.review_id) !== String(reviewData.id)
    )
    .map((campaign) => ({
      label: `${t("formatter:date", { value: new Date(campaign.period_start) })} - ${t("formatter:date", { value: formatEndDate(new Date(campaign.period_end)) })}`,
      value: String(campaign.score.id),
    }));

  // UI transformation needed for the Combobox
  const formattedSimulationsGroup = simulationsData.map((simulation) => ({
    label: simulation.name,
    value: String(simulation.score.id),
  }));

  const handleSelection = (value: string) => {
    submit(
      {
        scoreId: value,
      },
      { method: "POST" }
    );
  };

  const comparedCategoryScores =
    actionData?.ok && actionData.comparedCategoryScores
      ? actionData.comparedCategoryScores
      : [];

  return (
    <>
      <Typography step="3" as="h1" weight="bold" className="mb-3xs">
        {t("review:comparison.internalComparison")}
      </Typography>
      <Typography step="0" className="text-grayscale-dark">
        {t("formatter:date", { value: new Date(periodStart) })}
        &nbsp;-&nbsp;
        {t("formatter:date", {
          value: formatEndDate(new Date(periodEnd)),
        })}
      </Typography>
      <CardBackground className="mt-md">
        <Table className="mt-sm table-fixed">
          <Header>
            <Row key="header">
              <ColumnHeaderCell>
                {t("review:comparison.internal.categoryColumn")}
              </ColumnHeaderCell>
              <ColumnHeaderCell>
                {t("formatter:date", { value: new Date(periodStart) })}
                &nbsp;-&nbsp;
                {t("formatter:date", {
                  value: formatEndDate(new Date(periodEnd)),
                })}
              </ColumnHeaderCell>
              <ColumnHeaderCell>
                <div className="max-w-5/6">
                  <Combobox
                    groups={[
                      {
                        groupName: "Bilans carbone",
                        items: formatteTrackingCampaignsGroup,
                      },
                      {
                        groupName: "Simulations",
                        items: formattedSimulationsGroup,
                      },
                    ]}
                    placeholder={t("review:comparison.internal.selectColumn")}
                    size="medium"
                    onChange={handleSelection}
                  />
                </div>
              </ColumnHeaderCell>
              <ColumnHeaderCell>
                {t("review:comparison.internal.differenceColumn")}
              </ColumnHeaderCell>
            </Row>
          </Header>
          <Body>
            {
              <InternalComparison
                currentCategoryScores={currentCategoryScoreData}
                comparedCategoryScores={comparedCategoryScores}
                allCategoriesHierarchized={rootCategoriesById}
              />
            }
          </Body>
        </Table>
      </CardBackground>
      <ReviewBanner linkUrl={`/reviews/${reviewId}/feedback`} />
    </>
  );
}

export default ReviewInternalComparison;
